import React, { createContext, useContext, useState, useCallback } from 'react';
import { authService } from '../services/api';
import { getPostLoginPath } from '../utils/authRoles';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const login = useCallback(async (credentials) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await authService.login(credentials);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);
      return { success: true, data, redirectTo: getPostLoginPath(data.user) };
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      const message = Array.isArray(apiErrors) && apiErrors.length > 0
        ? apiErrors.map((item) => item.msg || item.message).filter(Boolean).join(' ')
        : err.response?.data?.message || 'Invalid email or password';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (formData) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await authService.register(formData);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);
      const emailSent = Boolean(data.emailSent);
      const message = data.message
        || (emailSent
          ? 'Registration successful. Check your email for login details.'
          : 'Registration successful.');
      return { success: true, data, message, emailSent };
    } catch (err) {
      const message = err.response?.data?.message || 'Registration failed';
      const errors = err.response?.data?.errors;
      setError(message);
      return { success: false, message, errors };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, error, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
